class FabricSwatchesListController {
  constructor(owner) {
    this.owner = owner;
  }

  renderMenus() {
    this.renderMaterialMenu();
    this.renderProductMenu();
    this.renderSortMenu();
    this.syncFilterUI();
  }

  renderMaterialMenu() {
    const owner = this.owner;
    const menu = owner.filterMenus.material;
    if (!menu) return;

    menu.innerHTML = owner.state.materials.map((item) => `
      <button
        type="button"
        class="ctm-fabric-swatches__filter-menu-item ${owner.state.selectedFilters.materials.includes(item.key) ? 'is-active' : ''}"
        data-action="set-filter"
        data-filter="material"
        data-value="${item.key}"
        aria-pressed="${owner.state.selectedFilters.materials.includes(item.key) ? 'true' : 'false'}"
      >
        <span class="ctm-fabric-swatches__filter-check" aria-hidden="true"></span>
        <span>${owner._escapeHtml(item.name)} (${item.count})</span>
      </button>
    `).join('');
  }

  renderProductMenu() {
    const owner = this.owner;
    const menu = owner.filterMenus.product;
    if (!menu) return;

    const productMenuItems = this.getProductMenuItems();

    menu.innerHTML = productMenuItems.map((product) => `
      <button
        type="button"
        class="ctm-fabric-swatches__filter-menu-item ${owner.state.selectedFilters.products.includes(product.product_handle) ? 'is-active' : ''} ${product.disabled ? 'is-disabled' : ''}"
        data-action="set-filter"
        data-filter="product"
        data-value="${product.product_handle}"
        ${product.disabled ? 'disabled' : ''}
        aria-disabled="${product.disabled ? 'true' : 'false'}"
        aria-pressed="${owner.state.selectedFilters.products.includes(product.product_handle) ? 'true' : 'false'}"
      >
        <span class="ctm-fabric-swatches__filter-check" aria-hidden="true"></span>
        <span>${owner._escapeHtml(product.product_title)} (${product.menu_count})</span>
      </button>
    `).join('');
  }

  renderSortMenu() {
    const owner = this.owner;
    const menu = owner.filterMenus.sort;
    if (!menu) return;

    menu.innerHTML = (owner.data.sort_options || []).map((option) => `
      <button
        type="button"
        class="ctm-fabric-swatches__filter-menu-item ${owner.state.sortValue === option.value ? 'is-active' : ''}"
        data-action="set-sort"
        data-value="${option.value}"
        aria-pressed="${owner.state.sortValue === option.value ? 'true' : 'false'}"
      >
        <span>${option.label}</span>
      </button>
    `).join('');
  }

  setFilter(type, value) {
    const owner = this.owner;
    const list = owner.state.selectedFilters[`${type}s`] || [];
    const index = list.indexOf(value);
    if (index >= 0) {
      list.splice(index, 1);
    } else {
      list.push(value);
    }
    owner.state.selectedFilters[`${type}s`] = list;
    if (type === 'material') {
      this.pruneSelectedProductsByMaterials();
    }
    this.refresh();
  }

  clearFilter(type, value) {
    const owner = this.owner;
    const list = owner.state.selectedFilters[`${type}s`] || [];
    owner.state.selectedFilters[`${type}s`] = value
      ? list.filter((item) => item !== value)
      : [];
    if (type === 'material') {
      this.pruneSelectedProductsByMaterials();
    }
    owner._closeMenus();
    this.refresh();
  }

  clearAllFilters() {
    const owner = this.owner;
    owner.state.selectedFilters.materials = [];
    owner.state.selectedFilters.products = [];
    owner._closeMenus();
    this.resetCollectionVisibleCounts();
    this.refresh();
  }

  setSort(value) {
    const owner = this.owner;
    owner.state.sortValue = value;
    owner._closeMenus();
    this.resetCollectionVisibleCounts();
    this.refresh();
  }

  refresh() {
    this.renderMenus();
    this.applyVisibility();
    this.updateResults();
    this.owner._updateCardImages();
  }

  applyVisibility() {
    const owner = this.owner;
    const visibleProducts = this.visibleProducts();
    const fragment = document.createDocumentFragment();

    visibleProducts.forEach((product) => {
      const collection = owner.querySelector(`ctm-fabric-swatches-collection[data-handle="${CSS.escape(product.product_handle)}"]`);
      if (!collection) return;
      collection.hidden = false;
      this.applyCollectionVisibility(collection, product);
      fragment.appendChild(collection);
    });

    owner.collectionNodes.forEach((collection) => {
      collection.hidden = !visibleProducts.some((product) => product.product_handle === collection.dataset.handle);
    });

    owner.collectionsEl?.appendChild(fragment);
    owner.state.empty = visibleProducts.length === 0;
    if (owner.emptyEl) owner.emptyEl.hidden = !owner.state.empty;
  }

  loadMore(button) {
    const owner = this.owner;
    const collection = button.closest('ctm-fabric-swatches-collection');
    if (!collection) return;
    const handle = collection.dataset.handle;
    const product = owner._getProduct(handle);
    if (!product) return;
    const perRow = owner._getProductPerRow(product);
    const current = owner.state.collectionVisibleCounts[handle] || perRow;
    owner.state.collectionVisibleCounts[handle] = Math.min(current + perRow, product.sku_items.length);
    this.applyCollectionVisibility(collection, product);
  }

  resetCollectionVisibleCounts() {
    const owner = this.owner;
    owner.state.collectionVisibleCounts = {};
    owner.state.products.forEach((product) => {
      owner.state.collectionVisibleCounts[product.product_handle] = Math.min(owner._getProductPerRow(product), product.sku_items.length);
    });
  }

  getEnabledProductHandlesForProductMenu() {
    const selectedMaterials = this.owner.state.selectedFilters.materials || [];
    if (!selectedMaterials.length) return null;

    const selectedMaterialSet = new Set(selectedMaterials);
    return new Set(
      this.owner.state.products
        .filter((product) => selectedMaterialSet.has(product.material_key))
        .map((product) => product.product_handle)
    );
  }

  getProductMenuItems() {
    const owner = this.owner;
    const enabledProductHandles = this.getEnabledProductHandlesForProductMenu();

    return owner.state.products.map((product) => {
      const enabled = enabledProductHandles === null || enabledProductHandles.has(product.product_handle);

      return {
        ...product,
        disabled: !enabled,
        menu_count: enabled ? (product.available_sku_count || 0) : 0
      };
    });
  }

  pruneSelectedProductsByMaterials() {
    const enabledProductHandles = this.getEnabledProductHandlesForProductMenu();
    if (enabledProductHandles === null) return;

    this.owner.state.selectedFilters.products = this.owner.state.selectedFilters.products.filter((handle) => {
      return enabledProductHandles.has(handle);
    });
  }

  visibleProducts() {
    const owner = this.owner;
    const selectedMaterials = new Set(owner.state.selectedFilters.materials);
    const selectedProducts = new Set(owner.state.selectedFilters.products);

    const filteredProducts = owner.state.products.filter((product) => {
      if (selectedMaterials.size && !selectedMaterials.has(product.material_key)) return false;
      if (selectedProducts.size && !selectedProducts.has(product.product_handle)) return false;

      if (owner.state.viewMode === 'curtain' && product.hide_curtain) return false;
      if (owner.state.viewMode === 'fabric' && product.hide_fabric) return false;
      if (owner.state.viewMode === 'roman' && product.hide_roman) return false;

      return true;
    });

    return this.sortProducts(filteredProducts);
  }

  sortProducts(products) {
    const owner = this.owner;
    const sorted = [...products];
    const orderMap = new Map(owner.state.products.map((product, index) => [product.product_handle, index]));

    sorted.sort((a, b) => {
      if (owner.state.sortValue === 'latest' || owner.state.sortValue === 'created-descending') {
        const aLatest = this.parseLatestDate(a.is_latest);
        const bLatest = this.parseLatestDate(b.is_latest);
        if (aLatest !== bLatest) return bLatest - aLatest;
      }

      if (owner.state.sortValue === 'best-selling') {
        if ((a.available_sku_count || 0) !== (b.available_sku_count || 0)) {
          return (b.available_sku_count || 0) - (a.available_sku_count || 0);
        }
        if ((a.is_hot || 0) !== (b.is_hot || 0)) return (a.is_hot || 0) - (b.is_hot || 0);
      }

      return (orderMap.get(a.product_handle) || 0) - (orderMap.get(b.product_handle) || 0);
    });

    return sorted;
  }

  parseLatestDate(value) {
    if (!value || value === true || value === false) return 0;

    const text = String(value).trim();
    if (!text) return 0;

    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      const date = new Date(text);
      const timestamp = date.getTime();
      return Number.isFinite(timestamp) ? timestamp : 0;
    }

    return 0;
  }

  syncFilterUI() {
    const owner = this.owner;
    const materialCount = owner.state.selectedFilters.materials.length;
    const productCount = owner.state.selectedFilters.products.length;
    if (owner.filterCounts.material) {
      owner.filterCounts.material.textContent = materialCount ? `(${materialCount})` : '';
    }
    if (owner.filterCounts.product) {
      owner.filterCounts.product.textContent = productCount ? `(${productCount})` : '';
    }
    const materialLabel = owner.filterTriggers.material?.querySelector('.ctm-fabric-swatches__filter-trigger-text');
    const productLabel = owner.filterTriggers.product?.querySelector('.ctm-fabric-swatches__filter-trigger-text');
    if (materialLabel) {
      materialLabel.textContent = materialCount ? `Material (${materialCount})` : 'Material';
    }
    if (productLabel) {
      productLabel.textContent = productCount ? `Collection (${productCount})` : 'Collection';
    }
    if (owner.sortLabel) {
      const label = (owner.data.sort_options || []).find((option) => option.value === owner.state.sortValue)?.label || 'Featured';
      owner.sortLabel.textContent = label;
    }
  }

  updateResults() {
    const owner = this.owner;
    if (!owner.resultsEl) return;
    const chips = [];

    owner.state.selectedFilters.materials.forEach((key) => {
      const item = owner.state.materials.find((material) => material.key === key);
      if (!item) return;
      chips.push(`
        <span class="ctm-fabric-swatches__result-chip">
          <span>${item.name}</span>
          <button type="button" data-action="clear-filter" data-filter="material" data-value="${item.key}" aria-label="Remove ${owner._escapeHtml(item.name)} filter">
            ${owner._renderResultCloseIcon()}
          </button>
        </span>
      `);
    });

    owner.state.selectedFilters.products.forEach((handle) => {
      const item = owner.state.products.find((product) => product.product_handle === handle);
      if (!item) return;
      const title = item.product_title;
      chips.push(`
        <span class="ctm-fabric-swatches__result-chip">
          <span>${owner._escapeHtml(title)}</span>
          <button type="button" data-action="clear-filter" data-filter="product" data-value="${owner._escapeHtml(handle)}" aria-label="Remove ${owner._escapeHtml(title)} filter">
            ${owner._renderResultCloseIcon()}
          </button>
        </span>
      `);
    });

    if (chips.length) {
      chips.push(`
        <button class="ctm-fabric-swatches__result-clear" type="button" data-action="clear-all-filters">
          CLEAR
        </button>
      `);
    }

    owner.resultsEl.innerHTML = chips.join('');
  }

  applyCollectionVisibility(collection, product) {
    const owner = this.owner;
    const perRow = owner._getProductPerRow(product);
    const totalCount = product.sku_items.length;
    const visibleCount = Math.min(owner.state.collectionVisibleCounts[product.product_handle] || perRow || totalCount, totalCount);
    this.ensureCollectionItems(collection, product, visibleCount);

    const skuItems = Array.from(collection.querySelectorAll('.ctm-fabric-swatches-collection-item'));
    skuItems.forEach((card, index) => {
      card.hidden = index >= visibleCount;
    });

    const more = collection.querySelector('.ctm-fabric-swatches-collection-more');
    if (more) {
      const moreBtn = more.querySelector('.ctm-fabric-swatches-collection-more-btn');
      more.hidden = totalCount <= visibleCount;
      if (moreBtn) {
        const remaining = Math.max(0, totalCount - visibleCount);
        moreBtn.textContent = remaining > 0 ? 'LOAD MORE' : '';
      }
    }

    owner._syncCardImageAccessibility(collection);
  }

  ensureCollectionItems(collection, product, visibleCount) {
    const owner = this.owner;
    const list = collection.querySelector('.ctm-fabric-swatches-collection');
    if (!list) return;

    const renderedVariantIds = new Set(
      Array.from(list.querySelectorAll('.ctm-fabric-swatches-collection-item'))
        .map((card) => String(card.dataset.variantId || ''))
        .filter(Boolean)
    );

    const imageRatio = list.querySelector('.ctm-fabric-swatches-collection-item')?.style.getPropertyValue('--image_ratio') || '100%';
    let added = false;

    product.sku_items.slice(0, visibleCount).forEach((variant) => {
      const variantId = String(variant.variant_id || '');
      if (!variantId || renderedVariantIds.has(variantId)) return;

      list.insertAdjacentHTML('beforeend', owner._getCollectionItemMarkup(product, variant, imageRatio));
      renderedVariantIds.add(variantId);
      added = true;
    });

    if (added) {
      owner.cartController.syncSkuButtons({ skipModalRender: true });
    }
  }
}

class FabricSwatchesCartController {
  constructor(owner) {
    this.owner = owner;
  }

  bindCartUpdateEvents() {
    const owner = this.owner;
    if (window.FoxTheme?.pubsub?.subscribe && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      owner._cartUpdateUnsubscriber = window.FoxTheme.pubsub.subscribe(
        window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        owner._boundCartUpdate
      );
    }
    document.addEventListener('cart:updated', owner._boundCartUpdate);
  }

  async toggleSwatch(button) {
    const owner = this.owner;
    const item = button.closest('.ctm-fabric-swatches-sku-item');
    const card = button.closest('.ctm-fabric-swatches-collection-item');
    const variantId = Number(button.dataset.variantId || item?.dataset.variantId || card?.dataset.variantId || 0);
    if (!variantId) return;

    const isInCart = owner.state.cartItemIds.has(String(variantId));
    if (!isInCart && this.getBagCount() >= owner.constructor.swatchBagLimit) {
      this.syncSkuButtons();
      this.openBag();
      return;
    }

    button.disabled = true;
    owner._setSwatchButtonLoading(button, true);

    try {
      const sectionsToBundle = this.getCartSectionsToRender();
      const endpoint = isInCart ? owner.constructor.cartUpdateUrl : owner.constructor.cartAddUrl;
      const payload = isInCart
        ? { updates: { [variantId]: 0 }, sections: sectionsToBundle, sections_url: window.location.pathname }
        : { items: [{ id: variantId, quantity: 1 }], sections: sectionsToBundle, sections_url: window.location.pathname };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`cart request failed: ${response.status}`);
      }

      const parsedState = await response.json();
      await this.syncCartState({ sections: parsedState?.sections });
      document.dispatchEvent(new CustomEvent(isInCart ? 'ctm:swatch:removed' : 'ctm:swatch:added', {
        detail: {
          productHandle: item?.dataset.productHandle || null,
          variantId
        }
      }));
      this.renderBag();
      if (!isInCart) {
        if (this.getBagCount() >= owner.constructor.swatchBagLimit) {
          this.openCartDrawer();
        } else {
          this.openBag();
        }
      } else if (!this.getBagItems().length) {
        this.closeBag();
      }
    } catch (error) {
      console.error('[ctm-fabric-swatches] cart toggle failed', error);
    } finally {
      owner._setSwatchButtonLoading(button, false);
      this.syncSkuButtons();
    }
  }

  async syncCartState(options = {}) {
    const owner = this.owner;
    try {
      const response = await fetch(owner.constructor.cartJsonUrl, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      if (!response.ok) {
        throw new Error(`cart sync failed: ${response.status}`);
      }
      const cart = await response.json();
      if (options.sections) {
        cart.sections = options.sections;
      }
      this.applyCartState(cart, { keepBagClosed: options.keepBagClosed !== false });
      this.publishCartUpdate(cart);
      if (!cart.sections && options.refreshDrawer !== false) {
        this.refreshCartDrawer(false);
      }
    } catch (error) {
      console.error('[ctm-fabric-swatches] cart sync failed', error);
    }
  }

  onExternalCartUpdate(event = {}) {
    const cart = event.cart || event.detail?.cart;
    if (!cart || cart.errors) return;

    const token = this.getCartStateToken(cart);
    if (token && token === this.owner._lastAppliedCartToken) return;

    this.applyCartState(cart, { keepBagClosed: true });
  }

  getBagCount() {
    return this.getBagItems().length;
  }

  getBagItems() {
    const owner = this.owner;
    const productsByVariantId = new Map();
    owner.state.products.forEach((product) => {
      product.sku_items.forEach((variant) => {
        productsByVariantId.set(String(variant.variant_id), { product, variant });
      });
    });

    return (owner.state.cart?.items || [])
      .map((cartItem) => {
        const match = productsByVariantId.get(String(cartItem.id));
        if (!match) return null;
        return {
          ...match,
          quantity: cartItem.quantity || 1
        };
      })
      .filter(Boolean);
  }

  openBag() {
    const owner = this.owner;
    if (!owner.bagEl) return;
    owner._lastBagFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    owner.state.bagVisible = true;
    owner.bagEl.hidden = false;
    owner.bagEl.setAttribute('aria-hidden', 'false');
    this.renderBag();
    const closeBtn = owner.bagEl.querySelector('[data-action="close-bag"]');
    closeBtn?.focus();
  }

  closeBag() {
    const owner = this.owner;
    if (!owner.bagEl) return;
    owner.state.bagVisible = false;
    owner.bagEl.hidden = true;
    owner.bagEl.setAttribute('aria-hidden', 'true');
    const bagContent = owner.bagEl.querySelector('[data-bag-content]');
    if (bagContent) {
      bagContent.replaceChildren();
    }
    if (owner._lastBagFocusedElement && typeof owner._lastBagFocusedElement.focus === 'function') {
      owner._lastBagFocusedElement.focus();
    }
    owner._lastBagFocusedElement = null;
  }

  openCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { open: true }
    }));
  }

  applyCartState(cart, options = {}) {
    const owner = this.owner;
    cart.item_count_display = this.getDisplayCartItemCount(cart);
    owner._lastAppliedCartToken = this.getCartStateToken(cart);
    owner.state.cart = cart;
    owner.state.cartItemIds = new Set((cart.items || []).map((item) => String(item.id)));
    this.syncSkuButtons();
    this.syncBagState(options);
    this.syncHeaderCartCount(cart);
  }

  getCartStateToken(cart) {
    return [
      cart?.item_count || 0,
      (cart?.items || []).map((item) => `${item.id}:${item.quantity || 0}`).join('|')
    ].join(':');
  }

  getCartSectionsToRender() {
    const sections = ['cart-items-num'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
      bubbles: true,
      detail: { sections }
    }));

    // document.querySelectorAll('[data-section-type="custom-header"][data-section-id]').forEach((header) => {
    //   sections.push(header.dataset.sectionId);
    // });

    return [...new Set(sections.filter(Boolean))];
  }

  syncHeaderCartCount(cart) {
    this.syncHeaderCartCountFromSections(cart?.sections);
    const count = this.getDisplayCartItemCount(cart);
    document.querySelectorAll('.cart-items-num').forEach((element) => {
      if (element.dataset.ctmServerSynced === 'true') {
        delete element.dataset.ctmServerSynced;
        return;
      }
      element.textContent = count > 99 ? '99+' : String(count);
    });
    document.documentElement.classList[count > 0 ? 'add' : 'remove']('cart-has-items');
  }

  getDisplayCartItemCount(cart) {
    const sectionCount = this.getDisplayCartItemCountFromSections(cart?.sections);
    if (sectionCount !== null) {
      return sectionCount;
    }

    const headerCount = Number(document.querySelector('.cart-items-num')?.textContent?.trim());
    if (Number.isFinite(headerCount)) {
      return headerCount;
    }

    return Number(cart?.item_count || 0);
  }

  getDisplayCartItemCountFromSections(sections) {
    if (!sections) return null;

    for (const sectionHTML of Object.values(sections)) {
      if (!sectionHTML) continue;

      const parsedHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const cartCount = Number(parsedHTML.querySelector('cart-count[data-item-count-display]')?.dataset.itemCountDisplay);
      if (Number.isFinite(cartCount)) {
        return cartCount;
      }

      const headerCount = Number(parsedHTML.querySelector('.cart-items-num')?.textContent?.trim());
      if (Number.isFinite(headerCount)) {
        return headerCount;
      }
    }

    return null;
  }

  syncHeaderCartCountFromSections(sections) {
    if (!sections) return;

    Object.entries(sections).forEach(([sectionId, sectionHTML]) => {
      if (!sectionId.includes('custom_header') || !sectionHTML) return;

      const parsedHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const updatedCounts = parsedHTML.querySelectorAll('.cart-items-num');
      const currentCounts = document.querySelectorAll('.cart-items-num');
      currentCounts.forEach((element, index) => {
        const updatedCount = updatedCounts[index] || updatedCounts[0];
        if (!updatedCount) return;

        element.innerHTML = updatedCount.innerHTML;
        element.dataset.ctmServerSynced = 'true';
      });
    });
  }

  publishCartUpdate(cart) {
    let published = false;
    if (window.FoxTheme?.pubsub?.publish && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
      published = true;
    }
    if (!published || typeof cart.sections === 'undefined') {
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart }
      }));
    }
  }

  refreshCartDrawer(open = false) {
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { open }
    }));
  }

  syncSkuButtons(options = {}) {
    const owner = this.owner;
    const limitReached = this.getBagCount() >= owner.constructor.swatchBagLimit;
    owner.querySelectorAll('[data-action="toggle-swatch"]').forEach((button) => {
      const item = button.closest('.ctm-fabric-swatches-sku-item, .ctm-fabric-swatches-collection-item');
      const variantId = button.dataset.variantId || item?.dataset.variantId;
      const inCart = owner.state.cartItemIds.has(String(variantId));
      const available = button.dataset.available !== 'false' && item?.dataset.available !== 'false';
      item?.classList.toggle('is-in-cart', inCart);
      button.classList.toggle('is-in-cart', inCart);
      if (!available) {
        owner._setSwatchButtonLabel(button, 'Sold Out');
        button.disabled = true;
        button.classList.remove('ctm-swatch__disabled');
        return;
      }
      owner._setSwatchButtonLabel(button, inCart ? 'Remove' : 'Add Swatch');
      button.disabled = limitReached && !inCart;
      button.classList.toggle('ctm-swatch__disabled', limitReached && !inCart);
    });
    if (!options.skipModalRender) {
      owner.modalController.renderIfOpen();
    }
  }

  syncBagState(options = {}) {
    const owner = this.owner;
    if (!owner.bagEl) return;

    const hasBagItems = this.getBagItems().length > 0;
    if (!hasBagItems) {
      this.closeBag();
      return;
    }

    if (!options.keepBagClosed || owner.state.bagVisible || !owner.bagEl.hidden) {
      this.renderBag();
    }
  }

  renderBag() {
    const owner = this.owner;
    if (!owner.bagEl) return;
    const bagItems = this.getBagItems();

    if (!bagItems.length) {
      this.closeBag();
      return;
    }

    owner.bagEl.hidden = false;
    owner.bagEl.setAttribute('aria-hidden', 'false');
    owner.state.bagVisible = true;
    const bagTitle = owner.bagEl.querySelector('[data-bag-title]');
    const bagContent = owner.bagEl.querySelector('[data-bag-content]');
    if (!bagTitle || !bagContent) return;

    const count = bagItems.reduce((total, item) => total + Number(item.quantity || 1), 0);
    bagTitle.textContent = `My Swatches (${count}/${owner.constructor.swatchBagLimit})`;

    const fragment = document.createDocumentFragment();
    bagItems.forEach(({ product, variant }) => {
      const image = variant.image || product.featured_image?.src || '';
      const title = variant.title || '';
      const itemEl = document.createElement('div');
      itemEl.className = 'ctm-fabric-swatches__bag-item';
      itemEl.setAttribute('role', 'listitem');

      if (image) {
        const imageEl = document.createElement('img');
        imageEl.className = 'ctm-fabric-swatches__bag-image';
        imageEl.src = image;
        imageEl.alt = title;
        imageEl.loading = 'lazy';
        itemEl.appendChild(imageEl);
      } else {
        const fallbackEl = document.createElement('span');
        fallbackEl.className = 'ctm-fabric-swatches__bag-fallback';
        fallbackEl.setAttribute('role', 'img');
        fallbackEl.setAttribute('aria-label', title);
        itemEl.appendChild(fallbackEl);
      }

      fragment.appendChild(itemEl);
    });

    bagContent.replaceChildren(fragment);
  }
}

class FabricSwatchesModalController {
  constructor(owner) {
    this.owner = owner;
  }

  open(productHandle, variantId = null) {
    const owner = this.owner;
    const product = owner._getProduct(productHandle);
    if (!product) return;
    owner.state.activeProductHandle = productHandle;
    const variantIndex = variantId
      ? product.sku_items.findIndex((variant) => String(variant.variant_id) === String(variantId))
      : -1;
    if (variantId) {
      owner.state.activeVariants[productHandle] = Number(variantId);
    }
    owner.state.modalCarouselIndex = Math.max(0, variantIndex >= 0 ? variantIndex : product.sku_items.findIndex((variant) => variant.selected));
    if (owner.state.modalCarouselIndex < 0) {
      owner.state.modalCarouselIndex = 0;
    }
    owner._lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.render(product);
    const closeBtn = owner.modalEl.querySelector('[data-action="close-modal"]');
    closeBtn?.focus();
  }

  cacheShellElements() {
    const owner = this.owner;
    owner.modalPanel = owner.modalEl?.querySelector('.ctm-fabric-swatches__modal-panel') || null;
    owner.modalTitleEl = owner.modalEl?.querySelector('[data-modal-title]') || null;
    owner.modalCarouselViewport = owner.modalEl?.querySelector('[data-modal-viewport]') || null;
    owner.modalCarouselTrack = owner.modalEl?.querySelector('[data-modal-track]') || null;
    owner.modalPrevBtn = owner.modalEl?.querySelector('[data-action="modal-prev"]') || null;
    owner.modalNextBtn = owner.modalEl?.querySelector('[data-action="modal-next"]') || null;
  }

  ensureShell() {
    const owner = this.owner;
    if (!owner.modalEl) return false;
    this.cacheShellElements();
    return Boolean(owner.modalPanel && owner.modalCarouselTrack);
  }

  getTitleId() {
    return `ctm-fabric-swatches-modal-title-${this.owner.dataset.sectionId || 'default'}`;
  }

  close() {
    const owner = this.owner;
    if (!owner.modalEl) return;
    this.destroySwiper();
    if (owner.modalCarouselTrack) {
      owner.modalCarouselTrack.innerHTML = '';
    }
    owner.modalPanel?.removeAttribute('aria-label');
    owner.modalPanel = null;
    owner.modalTitleEl = null;
    owner.modalCarouselViewport = null;
    owner.modalCarouselTrack = null;
    owner.modalCarouselSlides = [];
    owner.modalPrevBtn = null;
    owner.modalNextBtn = null;
    owner.modalEl.hidden = true;
    owner.modalEl.setAttribute('aria-hidden', 'true');
    if (owner._lastFocusedElement && typeof owner._lastFocusedElement.focus === 'function') {
      owner._lastFocusedElement.focus();
    }
    owner._lastFocusedElement = null;
  }

  renderIfOpen() {
    const owner = this.owner;
    if (!owner.modalEl || owner.modalEl.hidden || !owner.state.activeProductHandle) return;
    const product = owner._getProduct(owner.state.activeProductHandle);
    if (product) this.render(product);
  }

  setCarouselIndex(index, options = {}) {
    const owner = this.owner;
    const swiper = owner.modalSwiper?.slider;
    if (!swiper) return;

    const slides = this.getSlides();
    if (!slides.length) return;
    const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
    const speed = options.behavior === 'auto' ? 0 : undefined;
    if (typeof swiper.slideToLoop === 'function') {
      swiper.slideToLoop(nextIndex, speed);
      return;
    }
    swiper.slideTo(nextIndex, speed);
  }

  moveCarousel(step) {
    const swiper = this.owner.modalSwiper?.slider;
    if (!swiper) return;

    if (step > 0) swiper.slideNext();
    if (step < 0) swiper.slidePrev();
  }

  getSlides() {
    if (this.owner.modalCarouselSlides?.length) return this.owner.modalCarouselSlides;
    return this.getAllSlides().filter((slide) => !slide.classList.contains('swiper-slide-duplicate'));
  }

  getAllSlides() {
    return Array.from(this.owner.modalCarouselTrack?.querySelectorAll('[data-modal-slide]') || []);
  }

  syncCarouselState(index) {
    const owner = this.owner;
    const slides = this.getSlides();
    if (!slides.length) return;
    const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
    owner.state.modalCarouselIndex = nextIndex;

    const allSlides = this.getAllSlides();
    const hasSwiperActiveSlide = allSlides.some((slide) => slide.classList.contains('swiper-slide-active'));
    allSlides.forEach((slide) => {
      const isTargetIndex = Number(slide.dataset.modalSlideIndex || -1) === nextIndex;
      const isActive = isTargetIndex && (!hasSwiperActiveSlide || slide.classList.contains('swiper-slide-active'));
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    if (owner.modalPrevBtn) {
      owner.modalPrevBtn.disabled = slides.length <= 1;
    }
    if (owner.modalNextBtn) {
      owner.modalNextBtn.disabled = slides.length <= 1;
    }

    const activeSlide = slides[nextIndex];
    if (activeSlide) {
      this.ensureImagesAround(nextIndex);
      const variantId = Number(activeSlide.dataset.variantId || 0);
      if (variantId && owner.state.activeProductHandle) {
        owner.state.activeVariants[owner.state.activeProductHandle] = variantId;
        owner._updateCardImages();
      }
    }

    return nextIndex;
  }

  ensureSlideImageLoaded(slide) {
    const image = slide?.querySelector?.('.ctm-fabric-swatches__modal-slide-image');
    const deferredSrc = image?.dataset?.src || '';
    if (!image || !deferredSrc || image.getAttribute('src')) return;

    image.setAttribute('src', deferredSrc);
  }

  ensureImagesAround(index, radius = 1) {
    const slides = this.getSlides();
    const total = slides.length;
    if (!total) return;
    const targetIndexes = new Set();

    for (let offset = -radius; offset <= radius; offset += 1) {
      const targetIndex = (index + offset + total) % total;
      targetIndexes.add(targetIndex);
    }

    this.getAllSlides().forEach((slide) => {
      if (targetIndexes.has(Number(slide.dataset.modalSlideIndex || -1))) {
        this.ensureSlideImageLoaded(slide);
      }
    });
  }

  destroySwiper() {
    const owner = this.owner;
    const slider = owner.modalSwiper?.slider;
    if (slider && !slider.destroyed) {
      slider.destroy(true, true);
    }
    owner.modalSwiper = null;
  }

  initSwiper(initialIndex = 0) {
    const owner = this.owner;
    if (!owner.modalCarouselViewport || !window.FoxTheme?.Carousel) return;

    const slides = this.getSlides();
    if (!slides.length) return;

    this.destroySwiper();

    const swiperOptions = {
      slidesPerView: 1,
      spaceBetween: 0,
      initialSlide: initialIndex,
      loop: slides.length > 1,
      rewind: false,
      threshold: 2,
      watchSlidesProgress: true,
      navigation: {
        nextEl: owner.modalNextBtn,
        prevEl: owner.modalPrevBtn
      },
      a11y: {
        enabled: true,
        containerMessage: 'SKU carousel',
        prevSlideMessage: 'Previous SKU',
        nextSlideMessage: 'Next SKU'
      },
      on: {
        init: (swiper) => {
          this.syncCarouselState(Number.isInteger(swiper.realIndex) ? swiper.realIndex : initialIndex);
        },
        slideChange: (swiper) => {
          this.syncCarouselState(Number.isInteger(swiper.realIndex) ? swiper.realIndex : swiper.activeIndex);
        }
      }
    };

    owner.modalSwiper = new window.FoxTheme.Carousel(owner.modalCarouselViewport, swiperOptions);
    owner.modalSwiper.init();
  }

  getVisibleVariants(product) {
    const owner = this.owner;
    const collection = owner.querySelector(`ctm-fabric-swatches-collection[data-handle="${CSS.escape(product.product_handle)}"]`);
    const variantsById = new Map(product.sku_items.map((variant) => [String(variant.variant_id), variant]));
    const visibleVariants = Array.from(collection?.querySelectorAll('.ctm-fabric-swatches-collection-item') || [])
      .filter((card) => !card.hidden)
      .map((card) => variantsById.get(String(card.dataset.variantId || '')))
      .filter(Boolean);

    if (visibleVariants.length) return visibleVariants;

    const perRow = owner._getProductPerRow(product);
    const visibleCount = Math.min(owner.state.collectionVisibleCounts[product.product_handle] || perRow, product.sku_items.length);
    return product.sku_items.slice(0, visibleCount);
  }

  render(product) {
    const owner = this.owner;
    const activeVariant = owner._getActiveVariant(product);
    const visibleVariants = this.getVisibleVariants(product);
    const slideCount = visibleVariants.length;
    if (!slideCount) return;
    const activeVariantIndex = Math.max(
      0,
      visibleVariants.findIndex((variant) => String(variant.variant_id) === String(activeVariant?.variant_id || ''))
    );
    const initialDisplayIndex = activeVariantIndex;
    const activeVariantId = String(visibleVariants[initialDisplayIndex]?.variant_id || '');

    const renderSlide = (variant, displayIndex) => {
      const isInCart = owner.state.cartItemIds.has(String(variant.variant_id));
      const limitReached = owner.cartController.getBagCount() >= owner.constructor.swatchBagLimit;
      const disabledByLimit = limitReached && !isInCart;
      const isSelected = String(variant.variant_id) === activeVariantId;
      const title = owner._escapeHtml(variant.title || '');
      const image = owner._getVariantDisplayImage(product, variant);
      const distanceToActive = Math.abs(displayIndex - initialDisplayIndex);
      const shouldLoadNow = Math.min(distanceToActive, Math.max(0, slideCount - distanceToActive)) <= 1;
      const loadingMode = displayIndex === initialDisplayIndex ? 'eager' : 'lazy';
      return `
        <div
          class="ctm-fabric-swatches__modal-slide ctm-fabric-swatches-sku-item swiper-slide ${variant.available ? '' : 'is-disabled'} ${isSelected ? 'is-selected' : ''} ${isInCart ? 'is-in-cart' : ''}"
          data-product-handle="${product.product_handle}"
          data-variant-id="${variant.variant_id}"
          data-sku="${owner._escapeHtml(variant.sku || '')}"
          data-available="${variant.available}"
          data-modal-slide
          data-modal-slide-index="${displayIndex}"
          role="group"
          aria-roledescription="slide"
          aria-label="Slide ${displayIndex + 1} of ${slideCount}"
          aria-hidden="${displayIndex === initialDisplayIndex ? 'false' : 'true'}"
        >
          <div class="ctm-fabric-swatches__modal-slide-media">
            ${image ? `<img class="ctm-fabric-swatches__modal-slide-image" ${shouldLoadNow ? `src="${image}" ` : ''}data-src="${image}" alt="${title}" loading="${loadingMode}" ${displayIndex === initialDisplayIndex ? 'fetchpriority="high"' : ''}>` : `<span class="ctm-fabric-swatches__modal-slide-fallback" aria-hidden="true"></span>`}
          </div>
          <div class="ctm-fabric-swatches__modal-slide-body">
            <div class="ctm-fabric-swatches__modal-slide-product-title">${owner._escapeHtml(product.product_title)}</div>
            <div class="ctm-fabric-swatches__modal-slide-title">${title}</div>
            <button
              type="button"
              class="ctm-fabric-swatches-sku-item__add-btn ${disabledByLimit ? 'ctm-swatch__disabled' : ''}"
              data-action="toggle-swatch"
              ${variant.available && !disabledByLimit ? '' : 'disabled'}
              data-variant-id="${variant.variant_id}"
              data-available="${variant.available}"
              aria-label="${variant.available ? (isInCart ? `Remove ${title}` : `Add swatch for ${title}`) : `Sold Out ${title}`}"
            >
              ${owner._getSwatchButtonMarkup(variant.available ? (isInCart ? 'Remove' : 'Add Swatch') : 'Sold Out')}
            </button>
          </div>
        </div>
      `;
    };

    const slides = visibleVariants.map((variant, displayIndex) => renderSlide(variant, displayIndex)).join('');

    if (!this.ensureShell()) return;

    this.destroySwiper();
    owner.modalPanel?.setAttribute('aria-label', product.product_title || '');
    owner.modalCarouselTrack.innerHTML = slides;
    owner.modalCarouselSlides = Array.from(owner.modalEl.querySelectorAll('[data-modal-slide]'));
    owner.modalEl.hidden = false;
    owner.modalEl.setAttribute('aria-hidden', 'false');
    this.ensureImagesAround(initialDisplayIndex);
    this.initSwiper(initialDisplayIndex);
  }
}

class CustomFabricSwatches extends HTMLElement {
  static get cartAddUrl() {
    return `${window.Shopify?.routes?.root || '/'}cart/add.js`;
  }

  static get cartUpdateUrl() {
    return `${window.Shopify?.routes?.root || '/'}cart/update.js`;
  }

  static get cartJsonUrl() {
    return `${window.Shopify?.routes?.root || '/'}cart.js`;
  }

  static get swatchBagLimit() {
    return 5;
  }

  static safeJsonParse(text, fallback = {}) {
    try {
      return JSON.parse(text);
    } catch {
      return fallback;
    }
  }

  constructor() {
    super();
    this.initialized = false;
    this.data = {};
    this.listController = new FabricSwatchesListController(this);
    this.cartController = new FabricSwatchesCartController(this);
    this.modalController = new FabricSwatchesModalController(this);
    this._boundClick = this._onClick.bind(this);
    this._boundDocumentClick = this._onDocumentClick.bind(this);
    this._boundKeyDown = this._onKeyDown.bind(this);
    this._boundCartUpdate = this.cartController.onExternalCartUpdate.bind(this.cartController);
    this._boundTabIndicatorUpdate = () => this._scheduleTabIndicatorUpdate(false);
    this._boundTabTouchStart = this._onTabTouchStart.bind(this);
    this._boundTabTouchMove = this._onTabTouchMove.bind(this);
    this._boundTabTouchEnd = this._onTabTouchEnd.bind(this);
    this._cartUpdateUnsubscriber = null;
    this._lastAppliedCartToken = '';
    this.state = {
      products: [],
      materials: [],
      sortValue: 'featured',
      viewMode: 'fabric',
      rows: 8,
      loadMoreStep: 8,
      selectedFilters: {
        materials: [],
        products: []
      },
      activeVariants: {},
      cartItemIds: new Set(),
      cart: null,
      activeProductHandle: null,
      modalCarouselIndex: 0,
      empty: false,
      collectionVisibleCounts: {},
      bagVisible: false
    };
    this._lastFocusedElement = null;
    this._lastBagFocusedElement = null;
    this.modalCarouselViewport = null;
    this.modalCarouselTrack = null;
    this.modalCarouselSlides = [];
    this.modalSwiper = null;
    this._tabIndicatorRaf = null;
    this._tabSwipeRaf = null;
    this._tabSwipe = null;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this._cacheElements();
    this._readData();
    this._buildState();
    this.listController.renderMenus();
    this._bindEvents();
    this.cartController.bindCartUpdateEvents();
    this._applyRows();
    this._applyViewMode(this.state.viewMode, false);
    this._bindTabIndicatorEvents();
    this.listController.applyVisibility();
    this.cartController.syncCartState({ keepBagClosed: false });
  }

  disconnectedCallback() {
    this.modalController.destroySwiper();
    this.removeEventListener('click', this._boundClick);
    document.removeEventListener('click', this._boundDocumentClick);
    document.removeEventListener('keydown', this._boundKeyDown);
    document.removeEventListener('cart:updated', this._boundCartUpdate);
    if (this._cartUpdateUnsubscriber) {
      this._cartUpdateUnsubscriber();
      this._cartUpdateUnsubscriber = null;
    }
    window.removeEventListener('resize', this._boundTabIndicatorUpdate);
    if (this.tabsEl) {
      this.tabsEl.removeEventListener('touchstart', this._boundTabTouchStart);
      this.tabsEl.removeEventListener('touchmove', this._boundTabTouchMove);
      this.tabsEl.removeEventListener('touchend', this._boundTabTouchEnd);
      this.tabsEl.removeEventListener('touchcancel', this._boundTabTouchEnd);
    }
    if (this._tabIndicatorRaf) {
      cancelAnimationFrame(this._tabIndicatorRaf);
      this._tabIndicatorRaf = null;
    }
    if (this._tabSwipeRaf) {
      cancelAnimationFrame(this._tabSwipeRaf);
      this._tabSwipeRaf = null;
    }
  }

  _cacheElements() {
    this.dataScript = this.querySelector('[data-ctm-fabric-swatches]');
    this.resultsEl = this.querySelector('.ctm-fabric-swatches__results');
    this.collectionsEl = this.querySelector('.ctm-fabric-swatches__collections');
    this.emptyEl = this.querySelector('.ctm-fabric-swatches__empty');
    this.modalEl = this.querySelector('.ctm-fabric-swatches__modal');
    this.bagEl = this.querySelector('.ctm-fabric-swatches__bag');
    this.closeIconTemplate = this.querySelector('[data-ctm-icon-close]');
    this.modalPanel = null;
    this.modalTitleEl = null;
    this.filterMenus = {
      material: this.querySelector('[data-filter-menu="material"]'),
      product: this.querySelector('[data-filter-menu="product"]'),
      sort: this.querySelector('[data-filter-menu="sort"]')
    };
    this.filterTriggers = {
      material: this.querySelector('[data-menu="material"]'),
      product: this.querySelector('[data-menu="product"]'),
      sort: this.querySelector('[data-menu="sort"]')
    };
    this.sortLabel = this.querySelector('[data-sort-label]');
    this.filterCounts = {
      material: this.querySelector('[data-filter-count="material"]'),
      product: this.querySelector('[data-filter-count="product"]')
    };
    this.tabButtons = Array.from(this.querySelectorAll('.ctm__filter-tab'));
    this.tabsEl = this.querySelector('.ctm-fabric-swatches__tabs');
    this.tabIndicator = this.querySelector('.ctm__filter-indicator');
    this.rowButtons = Array.from(this.querySelectorAll('[data-action="set-rows"]'));
    this.collectionNodes = Array.from(this.querySelectorAll('ctm-fabric-swatches-collection'));
  }

  _readData() {
    this.data = this.constructor.safeJsonParse(this.dataScript?.textContent?.trim() || '{}', {});
  }

  _buildState() {
    const products = Array.isArray(this.data.products) ? this.data.products : [];
    const materialsMap = new Map();

    this.state.products = products.map((product, index) => {
      const skuItems = Array.isArray(product.sku_items) ? product.sku_items : [];
      const availableSkuCount = skuItems.filter((item) => item.available).length;
      const activeVariant = skuItems.find((item) => item.selected) || skuItems.find((item) => item.available) || skuItems[0] || null;
      const perRow = Math.max(1, parseInt(product.per_row || this.state.loadMoreStep || 8, 10) || 8);

      if (product.material_key) {
        const key = product.material_key;
        const name = product.material_name || product.material_key;
        const current = materialsMap.get(key) || { key, name, count: 0 };
        current.count += 1;
        materialsMap.set(key, current);
      }

      if (activeVariant?.variant_id) {
        this.state.activeVariants[product.product_handle] = activeVariant.variant_id;
      }

      return {
        ...product,
        _index: index,
        per_row: perRow,
        sku_items: skuItems,
        available_sku_count: availableSkuCount
      };
    });

    this.state.materials = Array.from(materialsMap.values());
    this.state.sortValue = this.data.sort_options?.[0]?.value || 'featured';
    this.state.viewMode = this.data.default_view_mode || 'fabric';
    this.state.rows = parseInt(this.data.default_rows || 8, 10);
    this.state.loadMoreStep = 8;
    this.state.selectedFilters.materials = [];
    this.state.selectedFilters.products = [];
    this.listController.resetCollectionVisibleCounts();
  }

  _bindEvents() {
    this.addEventListener('click', this._boundClick);
    document.addEventListener('click', this._boundDocumentClick);
    document.addEventListener('keydown', this._boundKeyDown);
  }

  _bindTabIndicatorEvents() {
    this._scheduleTabIndicatorUpdate(false);
    window.addEventListener('resize', this._boundTabIndicatorUpdate);

    if (!this.tabsEl) return;
    this.tabsEl.addEventListener('touchstart', this._boundTabTouchStart, { passive: true });
    this.tabsEl.addEventListener('touchmove', this._boundTabTouchMove, { passive: true });
    this.tabsEl.addEventListener('touchend', this._boundTabTouchEnd);
    this.tabsEl.addEventListener('touchcancel', this._boundTabTouchEnd);
  }

  _onClick(event) {
    const toggleMenuBtn = event.target.closest('[data-action="toggle-menu"]');
    if (toggleMenuBtn) {
      event.preventDefault();
      event.stopPropagation();
      const menuName = toggleMenuBtn.dataset.menu;
      this._toggleMenu(menuName);
      return;
    }

    const filterItem = event.target.closest('[data-action="set-filter"]');
    if (filterItem) {
      event.preventDefault();
      event.stopPropagation();
      this.listController.setFilter(filterItem.dataset.filter, filterItem.dataset.value);
      return;
    }

    const sortItem = event.target.closest('[data-action="set-sort"]');
    if (sortItem) {
      event.preventDefault();
      this.listController.setSort(sortItem.dataset.value);
      return;
    }

    const viewTab = event.target.closest('[data-action="set-view-mode"]');
    if (viewTab) {
      event.preventDefault();
      this._applyViewMode(viewTab.dataset.value, true);
      return;
    }

    const rowToggle = event.target.closest('[data-action="set-rows"]');
    if (rowToggle) {
      event.preventDefault();
      this._setRows(parseInt(rowToggle.dataset.rows, 10));
      return;
    }

    const loadMore = event.target.closest('[data-action="load-more"]');
    if (loadMore) {
      event.preventDefault();
      this.listController.loadMore(loadMore);
      return;
    }

    const clearFilter = event.target.closest('[data-action="clear-filter"]');
    if (clearFilter) {
      event.preventDefault();
      this.listController.clearFilter(clearFilter.dataset.filter, clearFilter.dataset.value);
      return;
    }

    const clearAll = event.target.closest('[data-action="clear-all-filters"]');
    if (clearAll) {
      event.preventDefault();
      this.listController.clearAllFilters();
      return;
    }

    const selectSku = event.target.closest('[data-action="select-sku"]');
    if (selectSku) {
      event.preventDefault();
      this._selectSku(selectSku);
      return;
    }

    const toggleSwatch = event.target.closest('[data-action="toggle-swatch"]');
    if (toggleSwatch) {
      event.preventDefault();
      this.cartController.toggleSwatch(toggleSwatch);
      return;
    }

    const swapImage = event.target.closest('[data-action="swap-card-image"]');
    if (swapImage) {
      event.preventDefault();
      this._swapCardImage(swapImage);
      return;
    }

    const openModal = event.target.closest('[data-action="open-modal"]');
    if (openModal) {
      event.preventDefault();
      const card = openModal.closest('.ctm-fabric-swatches-collection-item');
      const swatchImage = card ? this._getClickedSwatchImage(event, card) : null;
      if (swatchImage) {
        this._swapCardImage(swatchImage);
        return;
      }
      if (card) this.modalController.open(card.dataset.productHandle, card.dataset.variantId);
      return;
    }

    const closeModal = event.target.closest('[data-action="close-modal"]');
    if (closeModal) {
      event.preventDefault();
      this.modalController.close();
      return;
    }

    const closeBag = event.target.closest('[data-action="close-bag"]');
    if (closeBag) {
      event.preventDefault();
      this.cartController.closeBag();
      return;
    }

    const openCart = event.target.closest('[data-action="open-cart"]');
    if (openCart) {
      event.preventDefault();
      this.cartController.openCartDrawer();
      return;
    }

    if (event.target === this.modalEl) {
      event.preventDefault();
      this.modalController.close();
    }
  }

  _onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this._closeMenus();
      return;
    }
    if (!event.target.closest('.ctm-fabric-swatches__filter')) {
      this._closeMenus();
    }
  }

  _onKeyDown(event) {
    const swapImage = event.target.closest?.('[data-action="swap-card-image"]');
    if (swapImage && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this._swapCardImage(swapImage);
      return;
    }

    if (event.key === 'Escape') {
      if (!this.bagEl?.hidden) {
        this.cartController.closeBag();
        return;
      }
      this._closeMenus();
      this.modalController.close();
      return;
    }

    if (!this.modalEl?.hidden) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.modalController.moveCarousel(-1);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.modalController.moveCarousel(1);
        return;
      }
      if (event.key === 'Home') {
        event.preventDefault();
        this.modalController.setCarouselIndex(0);
        return;
      }
      if (event.key === 'End' && this.modalCarouselSlides.length) {
        event.preventDefault();
        this.modalController.setCarouselIndex(this.modalCarouselSlides.length - 1);
        return;
      }
    }

    if (event.key === 'Tab' && !this.modalEl?.hidden) {
      this._trapModalFocus(event);
      return;
    }

    if (event.key === 'Tab' && !this.bagEl?.hidden) {
      this._trapBagFocus(event);
    }
  }

  _toggleMenu(name) {
    Object.entries(this.filterMenus).forEach(([key, menu]) => {
      const trigger = this.filterTriggers[key];
      if (!menu || !trigger) return;
      const isTarget = key === name;
      const willOpen = isTarget && menu.hidden;
      menu.hidden = !willOpen;
      trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });
  }

  _closeMenus() {
    Object.entries(this.filterMenus).forEach(([key, menu]) => {
      const trigger = this.filterTriggers[key];
      if (!menu || !trigger) return;
      menu.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  _applyViewMode(value, fromUser = false) {
    this.state.viewMode = value;
    this.setAttribute('data-view-mode', value);
    this._syncTabs();
    this._resetCardImageState();
    this.listController.applyVisibility();
    this.modalController.renderIfOpen();
    if (fromUser) this._closeMenus();
  }

  _setRows(rows) {
    this.state.rows = rows;
    this._applyRows();
    this.listController.refresh();
  }

  _applyRows() {
    this.setAttribute('data-rows', String(this.state.rows));
    if (this.collectionsEl) {
      this.collectionsEl.setAttribute('data-rows', String(this.state.rows));
      this.collectionsEl.classList.toggle('four-rows', this.state.rows === 4);
    }
    this.rowButtons.forEach((button) => {
      button.classList.toggle('active', parseInt(button.dataset.rows, 10) === this.state.rows);
    });
  }

  _syncTabs() {
    this.tabButtons.forEach((button) => {
      const active = button.dataset.value === this.state.viewMode;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    this._scheduleTabIndicatorUpdate();
  }

  _scheduleTabIndicatorUpdate(animate = true) {
    if (this._tabIndicatorRaf) cancelAnimationFrame(this._tabIndicatorRaf);
    this._tabIndicatorRaf = requestAnimationFrame(() => {
      this._tabIndicatorRaf = null;
      this._updateTabIndicator(animate);
    });
  }

  _updateTabIndicator(animate = true) {
    if (!this.tabIndicator || !this.tabButtons.length) return;
    const activeTab = this.tabButtons.find((button) => button.classList.contains('active'));
    if (!activeTab) return;

    if (!animate) this.tabIndicator.classList.add('no-transition');
    this.tabIndicator.style.width = `${activeTab.offsetWidth}px`;
    this.tabIndicator.style.transform = `translateX(${activeTab.offsetLeft}px)`;
    this.tabsEl?.classList.add('is-indicator-ready');

    if (!animate) {
      void this.tabIndicator.offsetWidth;
      this.tabIndicator.classList.remove('no-transition');
    }
  }

  _onTabTouchStart(event) {
    if (window.innerWidth > 960 || this.tabButtons.length <= 1 || !this.tabIndicator) return;

    const activeIndex = this.tabButtons.findIndex((button) => button.classList.contains('active'));
    if (activeIndex < 0) return;

    this._tabSwipe = {
      startX: event.touches[0].clientX,
      startY: event.touches[0].clientY,
      activeIndex,
      currentLeft: this.tabButtons[activeIndex].offsetLeft,
      tabs: this.tabButtons.map((button) => ({
        left: button.offsetLeft,
        width: button.offsetWidth
      })),
      isSwiping: false,
      isLocked: false
    };

    this.tabIndicator.classList.add('no-transition');
  }

  _onTabTouchMove(event) {
    const swipe = this._tabSwipe;
    if (!swipe || window.innerWidth > 960 || swipe.tabs.length <= 1) return;

    const dx = event.touches[0].clientX - swipe.startX;
    const dy = event.touches[0].clientY - swipe.startY;

    if (!swipe.isLocked) {
      if (Math.abs(dx) <= 8 && Math.abs(dy) <= 8) return;
      swipe.isLocked = true;
      swipe.isSwiping = Math.abs(dx) > Math.abs(dy);
    }
    if (!swipe.isSwiping) return;

    if (this._tabSwipeRaf) return;
    this._tabSwipeRaf = requestAnimationFrame(() => {
      this._tabSwipeRaf = null;
      const firstLeft = swipe.tabs[0].left;
      const lastLeft = swipe.tabs[swipe.tabs.length - 1].left;
      const totalSpan = lastLeft - firstLeft || 1;
      const dragRatio = dx / ((this.tabsEl?.offsetWidth || 1) * 0.3);
      let nextLeft = swipe.currentLeft + dragRatio * totalSpan;

      if (nextLeft < firstLeft) {
        nextLeft = firstLeft - (firstLeft - nextLeft) * 0.2;
      } else if (nextLeft > lastLeft) {
        nextLeft = lastLeft + (nextLeft - lastLeft) * 0.2;
      }

      this.tabIndicator.style.transform = `translateX(${nextLeft}px)`;
    });
  }

  _onTabTouchEnd() {
    const swipe = this._tabSwipe;
    this._tabSwipe = null;
    if (this._tabSwipeRaf) {
      cancelAnimationFrame(this._tabSwipeRaf);
      this._tabSwipeRaf = null;
    }
    if (this.tabIndicator) this.tabIndicator.classList.remove('no-transition');
    if (!swipe || !swipe.isSwiping || swipe.tabs.length <= 1) {
      this._scheduleTabIndicatorUpdate();
      return;
    }

    const currentTransform = this.tabIndicator?.style.transform || '';
    const match = currentTransform.match(/translateX\(([^)]+)px\)/);
    const currentLeft = match ? parseFloat(match[1]) : swipe.currentLeft;
    const diff = currentLeft - swipe.currentLeft;
    const avgTabWidth = (swipe.tabs[swipe.tabs.length - 1].left - swipe.tabs[0].left) / (swipe.tabs.length - 1) || 100;
    let targetIndex = swipe.activeIndex;

    if (Math.abs(diff) > avgTabWidth * 0.25) {
      if (diff > 0 && swipe.activeIndex < this.tabButtons.length - 1) {
        targetIndex = swipe.activeIndex + 1;
      } else if (diff < 0 && swipe.activeIndex > 0) {
        targetIndex = swipe.activeIndex - 1;
      }
    }

    const targetMode = this.tabButtons[targetIndex]?.dataset.value;
    if (targetMode && targetMode !== this.state.viewMode) {
      this._applyViewMode(targetMode, true);
    } else {
      this._scheduleTabIndicatorUpdate();
    }
  }

  _escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  _renderResultCloseIcon() {
    return this.closeIconTemplate?.innerHTML?.trim() || `
      <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 64 64">
        <path stroke="#202020" d="M19 17.61l27.12 27.13m0-27.12L19 44.74"></path>
      </svg>
    `;
  }

  _syncCardImageAccessibility(scope) {
    const cards = scope?.matches?.('.ctm-fabric-swatches-collection-item')
      ? [scope]
      : Array.from(scope?.querySelectorAll?.('.ctm-fabric-swatches-collection-item') || []);

    cards.forEach((card) => {
      const images = Array.from(card.querySelectorAll('.tfs-img'));
      if (!images.length) return;

      const largeImage = card.querySelector('.tfs-img.is-large');
      const title = card.dataset.variantTitle
        || card.querySelector('.ctm-fabric-swatches-collection-name')?.textContent?.trim()
        || 'Swatch';

      images.forEach((image) => {
        const role = image.dataset.role || 'fabric-image';
        const img = image.querySelector('img');
        const isSwatch = image.classList.contains('is-swatch') || (!largeImage && this._isDefaultCardSwatch(image, card));
        const isLarge = image.classList.contains('is-large')
          || (!largeImage && !isSwatch && getComputedStyle(image).display !== 'none');
        const roleLabel = role === 'curtain-image'
          ? 'curtain image'
          : role === 'roman-image'
            ? 'roman image'
            : 'fabric image';

        if (isSwatch) {
          image.setAttribute('role', 'button');
          image.setAttribute('tabindex', '0');
          image.setAttribute('aria-label', `Show ${roleLabel} for ${title}`);
          image.setAttribute('aria-hidden', 'false');
        } else {
          image.removeAttribute('role');
          image.setAttribute('tabindex', '-1');
          image.removeAttribute('aria-label');
          image.setAttribute('aria-hidden', isLarge ? 'false' : 'true');
        }

        if (img) {
          img.alt = isLarge ? title : '';
          img.setAttribute('aria-hidden', isLarge ? 'false' : 'true');
        }
      });
    });
  }

  _getProductPerRow(product) {
    return Math.max(1, parseInt(product?.per_row || this.state.loadMoreStep || 8, 10) || 8);
  }

  _getCollectionItemMarkup(product, variant, imageRatio = '100%') {
    const title = this._escapeHtml(variant.title || '');
    const productTitle = this._escapeHtml(product.product_title || '');
    const variantId = this._escapeHtml(variant.variant_id || '');
    const materialKey = this._escapeHtml(product.material_key || '');
    const sku = this._escapeHtml(variant.sku || '');
    const available = variant.available !== false;
    const romanImage = variant.fabric_swatches_related_roman_image || '';
    const curtainImage = variant.fabric_swatches_related_curtain_image || '';
    const fabricImage = variant.image || product.featured_image?.src || '';

    return `
      <article
        class="ctm-fabric-swatches-collection-item${romanImage ? ' has-roman-img' : ''}"
        data-product-handle="${this._escapeHtml(product.product_handle || '')}"
        data-variant-id="${variantId}"
        data-variant-title="${title}"
        data-sku="${sku}"
        data-material-key="${materialKey}"
        data-hide-curtain="${this._escapeHtml(product.hide_curtain || '')}"
        data-hide-fabric="${this._escapeHtml(product.hide_fabric || '')}"
        data-hide-roman="${this._escapeHtml(product.hide_roman || '')}"
        data-curtain-image="${this._escapeHtml(curtainImage)}"
        data-roman-image="${this._escapeHtml(romanImage)}"
        data-default-variant-id="${this._escapeHtml(product.default_variant_id || '')}"
        style="--image_ratio: ${this._escapeHtml(imageRatio)};"
      >
        <div class="ctm-fabric-swatches-collection-img">
          <button
            type="button"
            class="ctm-fabric-swatches-collection-view"
            data-action="open-modal"
            aria-label="View ${productTitle}"
          >
            ${this._renderViewIcon()}
            <span>View</span>
          </button>
          ${this._getCollectionImageMarkup('tfs-fabric-img', 'fabric-image', fabricImage, title)}
          ${this._getCollectionImageMarkup('tfs-main-img', 'curtain-image', curtainImage, title)}
          ${this._getCollectionImageMarkup('tfs-roman-img', 'roman-image', romanImage, title)}
        </div>
        <div class="ctm-fabric-swatches-collection-name">${title}</div>
        <button
          type="button"
          class="ctm-add-swatch"
          data-action="toggle-swatch"
          data-variant-id="${variantId}"
          data-available="${available}"
          aria-label="${available ? `Add swatch for ${title}` : `Sold Out ${title}`}"
          ${available ? '' : 'disabled'}
        >
          ${this._getSwatchButtonMarkup(available ? 'Add Swatch' : 'Sold Out')}
        </button>
      </article>
    `;
  }

  _getCollectionImageMarkup(className, role, image, title) {
    if (!image) return '';

    const escapedImage = this._escapeHtml(image);

    return `
      <ctm-img-lazy class="tfs-img ${className}" data-action="swap-card-image" data-role="${role}">
        <img
          src="${escapedImage}"
          srcset="${escapedImage}"
          data-srcset="${escapedImage}"
          alt="${title}"
          loading="lazy"
          class="hide tfs-img__media"
        >
      </ctm-img-lazy>
    `;
  }

  _renderViewIcon() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
        <path d="M.857,2.25A.856.856,0,0,0,0,3.107V5.679a.857.857,0,0,0,1.714,0V3.964H3.429a.857.857,0,0,0,0-1.714Zm.857,8.571a.857.857,0,1,0-1.714,0v2.571a.856.856,0,0,0,.857.857H3.429a.857.857,0,1,0,0-1.714H1.714ZM8.571,2.25a.857.857,0,0,0,0,1.714h1.714V5.679a.857.857,0,1,0,1.714,0V3.107a.856.856,0,0,0-.857-.857ZM12,10.821a.857.857,0,1,0-1.714,0v1.714H8.571a.857.857,0,1,0,0,1.714h2.571A.856.856,0,0,0,12,13.393Z" transform="translate(0 -2.25)" fill="currentColor"></path>
      </svg>
    `;
  }

  _updateCardImages() {
    this.state.products.forEach((product) => {
      const collection = this.querySelector(`ctm-fabric-swatches-collection[data-handle="${CSS.escape(product.product_handle)}"]`);
      if (!collection) return;

      const activeVariant = this._getActiveVariant(product);
      collection.querySelectorAll('.ctm-fabric-swatches-collection-item').forEach((card) => {
        const variantId = Number(card.dataset.variantId || 0);
        const isSelected = String(variantId) === String(activeVariant?.variant_id || '');
        const inCart = this.state.cartItemIds.has(String(variantId));

        card.classList.toggle('is-selected', isSelected);
        card.classList.toggle('is-in-cart', inCart);
      });

      this._syncCardImageAccessibility(collection);

    });
    this.cartController.syncSkuButtons({ skipModalRender: true });
  }

  _resetCardImageState() {
    this.querySelectorAll('.ctm-fabric-swatches-collection-item .tfs-img').forEach((image) => {
      image.classList.remove('is-large', 'is-swatch');
    });
  }

  _getProduct(handle) {
    return this.state.products.find((product) => product.product_handle === handle);
  }

  _swapCardImage(image) {
    const card = image.closest('.ctm-fabric-swatches-collection-item');
    if (!card) return;

    const isSwatch = image.classList.contains('is-swatch') || this._isDefaultCardSwatch(image, card);
    if (!isSwatch) return;

    const currentLarge = card.querySelector('.tfs-img.is-large') || this._getCurrentLargeCardImage(card, image);
    if (currentLarge) {
      currentLarge.classList.remove('is-large');
      currentLarge.classList.add('is-swatch');
    }

    image.classList.remove('is-swatch');
    image.classList.add('is-large');
    this._syncCardImageAccessibility(card);
    this.modalController.renderIfOpen();
  }

  _getClickedSwatchImage(event, card) {
    return Array.from(card.querySelectorAll('.tfs-img')).find((image) => {
      const isSwatch = image.classList.contains('is-swatch') || this._isDefaultCardSwatch(image, card);
      if (!isSwatch) return false;
      const rect = image.getBoundingClientRect();
      return event.clientX >= rect.left
        && event.clientX <= rect.right
        && event.clientY >= rect.top
        && event.clientY <= rect.bottom;
    }) || null;
  }

  _isDefaultCardSwatch(image, card) {
    if (image.classList.contains('is-large')) return false;

    if (this.state.viewMode === 'curtain') {
      return image.classList.contains('tfs-fabric-img');
    }

    if (this.state.viewMode === 'fabric') {
      return image.classList.contains('tfs-main-img');
    }

    if (this.state.viewMode === 'roman') {
      return card.classList.contains('has-roman-img')
        ? image.classList.contains('tfs-fabric-img')
        : image.classList.contains('tfs-main-img');
    }

    return false;
  }

  _getCurrentLargeCardImage(card, nextImage) {
    return Array.from(card.querySelectorAll('.tfs-img')).find((image) => {
      if (image === nextImage || image.classList.contains('is-swatch')) return false;
      const style = getComputedStyle(image);
      return style.display === 'block'
        && style.width !== '40px'
        && style.width !== '80px';
    });
  }

  _getImageSrc(imageElement) {
    const src = imageElement?.querySelector?.('img')?.currentSrc
      || imageElement?.querySelector?.('img')?.src
      || '';

    return this._getSizedImageUrl(src, 750);
  }

  _getSizedImageUrl(src, width) {
    if (!src) return '';

    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return src.replace(/([?&])width=\d+/, `$1width=${width}`);
    }
  }

  _getVariantCard(productHandle, variantId) {
    if (!productHandle || !variantId) return null;
    return this.querySelector(
      `.ctm-fabric-swatches-collection-item[data-product-handle="${CSS.escape(String(productHandle))}"][data-variant-id="${CSS.escape(String(variantId))}"]`
    );
  }

  _getDefaultDisplayRole(product, variant) {
    if (this.state.viewMode === 'curtain') {
      return variant.fabric_swatches_related_curtain_image ? 'curtain-image' : 'fabric-image';
    }

    if (this.state.viewMode === 'roman') {
      return variant.fabric_swatches_related_roman_image ? 'roman-image' : 'fabric-image';
    }

    return 'fabric-image';
  }

  _getVariantImageByRole(product, variant, role) {
    if (role === 'curtain-image') {
      return variant.fabric_swatches_related_curtain_image || variant.image || product.featured_image?.src || '';
    }

    if (role === 'roman-image') {
      return variant.fabric_swatches_related_roman_image || variant.image || product.featured_image?.src || '';
    }

    return variant.image || product.featured_image?.src || '';
  }

  _getVariantDisplayImage(product, variant) {
    const card = this._getVariantCard(product.product_handle, variant.variant_id);
    const largeImage = card?.querySelector?.('.tfs-img.is-large');
    const largeSrc = this._getImageSrc(largeImage);
    if (largeSrc) return largeSrc;

    const defaultRole = this._getDefaultDisplayRole(product, variant);
    const defaultImage = card?.querySelector?.(`[data-role="${defaultRole}"]`);
    const defaultSrc = this._getImageSrc(defaultImage);
    if (defaultSrc) return defaultSrc;

    return this._getSizedImageUrl(this._getVariantImageByRole(product, variant, defaultRole), 750);
  }

  _getActiveVariant(product) {
    const activeId = this.state.activeVariants[product.product_handle] || product.default_variant_id;
    return product.sku_items.find((item) => String(item.variant_id) === String(activeId))
      || product.sku_items.find((item) => item.selected)
      || product.sku_items[0]
      || null;
  }

  async _selectSku(button) {
    const item = button.closest('.ctm-fabric-swatches-sku-item');
    const productHandle = item?.dataset.productHandle;
    const variantId = item?.dataset.variantId;
    if (!productHandle || !variantId) return;

    this.state.activeVariants[productHandle] = Number(variantId);
    this.state.activeProductHandle = productHandle;
    const product = this._getProduct(productHandle);
    if (product) {
      const nextIndex = product.sku_items.findIndex((variant) => String(variant.variant_id) === String(variantId));
      if (nextIndex >= 0) {
        this.state.modalCarouselIndex = nextIndex;
      }
    }
    this._updateCardImages();
    this.modalController.renderIfOpen();
    document.dispatchEvent(new CustomEvent('ctm:swatch:selected', {
      detail: { productHandle, variantId: Number(variantId) }
    }));
  }

  _getSwatchButtonMarkup(label) {
    return `
      <span data-swatch-button-label>${this._escapeHtml(label)}</span>
      <div class="loading__spinner hidden">
        <svg
          aria-hidden="true"
          focusable="false"
          class="spinner icon icon--small"
          viewBox="0 0 66 66"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
  }

  _setSwatchButtonLabel(button, label) {
    const labelEl = button.querySelector('[data-swatch-button-label]');
    if (labelEl) {
      labelEl.textContent = label;
      return;
    }

    button.innerHTML = this._getSwatchButtonMarkup(label);
  }

  _setSwatchButtonLoading(button, loading) {
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) {
      spinner.classList.toggle('hidden', !loading);
    }

    button.classList.toggle('is-loading', loading);
    button.toggleAttribute('aria-busy', loading);
  }

  _trapModalFocus(event) {
    const focusables = Array.from(this.modalEl?.querySelectorAll([
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',')) || []).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);

    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  _trapBagFocus(event) {
    const focusables = Array.from(this.bagEl?.querySelectorAll([
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',')) || []).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);

    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('ctm-fabric-swatches')) {
  customElements.define('ctm-fabric-swatches', CustomFabricSwatches);
}
